import { useContext, useRef } from "react";

import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";

import { css } from "@emotion/react";

import { BannerView, colors } from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import DropzoneContext from "context/dropzone/dropzoneContext";
import { useFileSize } from "shared/hooks/useFileSize";
import { FILE_COUNT } from "shared/types/enums";

import { FrameProps } from "./types";

const Frame = ({
    isDragged,
    supportedFormats,
    getRootProps,
    getInputProps,
    isMultiple,
    isDisabled,
    tooltipText,
    isUploaded,
    maxSizeBytes
}: FrameProps) => {
    const { t } = useTranslation();

    const {
        blue700,
        blue100,
        gray50,
        gray200,
        gray700,
        textDisabled,
        textPlaceholder
    } = colors;

    const { messages, removeMessages } = useContext(DropzoneContext);

    const { renderFileSize } = useFileSize();

    const nodeRef1 = useRef(null);
    const nodeRef2 = useRef(null);

    const bgColor = isDragged ? blue100 : gray50;
    const bgColorHover = isDisabled ? gray50 : blue100;
    const borderColor = isDragged ? blue700 : gray200;
    const borderColorHover = isDisabled ? gray200 : blue700;
    const textColor = isDisabled ? textDisabled : gray700;

    const multipleCheck = isMultiple ? FILE_COUNT.Files : FILE_COUNT.File;
    const isNoSupportedFormats = supportedFormats.length === 0;

    const renderTitle = () => {
        if (isUploaded) {
            return t("Dialog##selected files");
        }

        return t(`Dropzone##add ${multipleCheck}`);
    };

    const getFileSelection = () => {
        if (isNoSupportedFormats) {
            return <div>{t("Dropzone##select file type")}</div>;
        } else {
            return (
                <>
                    <div>
                        {t(`Dropzone##or drop ${multipleCheck} to upload`)}
                    </div>

                    <div>
                        {t("Dropzone##supported formats")}{" "}
                        {supportedFormats.join(", ")}
                    </div>
                </>
            );
        }
    };

    return (
        <>
            <div
                css={css({
                    color: gray700,
                    fontSize: "14px",
                    fontWeight: "500",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    marginBottom: "4px"
                })}
            >
                {renderTitle()}
            </div>

            {messages?.map(({ status, title, description }) => (
                <BannerView
                    key={title}
                    bannerType={status}
                    title={title}
                    description={description}
                    close={removeMessages}
                    css={css({
                        marginBottom: "4px"
                    })}
                />
            ))}

            {!isUploaded ? (
                <TooltipGeneral
                    placement={TOOLTIP_PLACEMENT.Top}
                    title={tooltipText || ""}
                >
                    <div
                        {...getRootProps()}
                        css={css({
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            height: maxSizeBytes ? "156px" : "128px",
                            background: bgColor,
                            border: `2px dashed ${borderColor}`,
                            borderRadius: "8px",
                            padding: "22px 30px",
                            fontSize: "14px",
                            lineHeight: "20px",
                            letterSpacing: "0.1px",
                            textAlign: "center",
                            boxSizing: "border-box",
                            cursor: isDisabled ? "auto" : "pointer",
                            transition: "all 0.3s ease-in-out",

                            "&:hover": {
                                background: bgColorHover,
                                border: `2px dashed ${borderColorHover}`
                            },

                            ".on-drag-enter-enter, .on-drag-leave-enter": {
                                opacity: "0"
                            },

                            ".on-drag-enter-enter-active, .on-drag-leave-enter-active":
                                {
                                    opacity: "1",
                                    transition: "opacity 300ms ease-in-out"
                                },

                            ".on-drag-enter-exit, .on-drag-leave-exit": {
                                opacity: "1"
                            },

                            ".on-drag-enter-exit-active, .on-drag-leave-exit-active":
                                {
                                    opacity: "0"
                                }
                        })}
                    >
                        <CSSTransition
                            nodeRef={nodeRef1}
                            in={isDragged}
                            unmountOnExit
                            timeout={{ enter: 300 }}
                            classNames="on-drag-enter"
                        >
                            <div
                                ref={nodeRef1}
                                css={css({
                                    color: blue700,
                                    fontWeight: "500"
                                })}
                            >
                                {t(`Dropzone##drop ${multipleCheck} to upload`)}
                            </div>
                        </CSSTransition>

                        <CSSTransition
                            nodeRef={nodeRef2}
                            in={!isDragged}
                            unmountOnExit
                            timeout={{ enter: 300 }}
                            classNames="on-drag-leave"
                        >
                            <div ref={nodeRef2}>
                                <Button
                                    data-testid="select-file-button"
                                    color={BUTTON_COLORS.PrimarySubtle}
                                    size={BUTTON_SIZES.Small}
                                    variant={BUTTON_VARIANTS.TextOnly}
                                    disabled={isDisabled}
                                    css={css({
                                        marginBottom: "4px"
                                    })}
                                >
                                    {t(`Dropzone##select ${multipleCheck}`)}
                                </Button>

                                <div
                                    css={css({
                                        color: textColor,

                                        "& > div:first-of-type": {
                                            fontWeight: "500"
                                        }
                                    })}
                                >
                                    {getFileSelection()}

                                    {maxSizeBytes !== undefined && (
                                        <div
                                            css={css({
                                                color: isDisabled
                                                    ? textDisabled
                                                    : textPlaceholder,
                                                marginTop: "8px"
                                            })}
                                        >
                                            {t(`Dropzone##max file size`, {
                                                size: renderFileSize(
                                                    maxSizeBytes
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </CSSTransition>

                        <input {...getInputProps()} />
                    </div>
                </TooltipGeneral>
            ) : (
                <div></div>
            )}
        </>
    );
};

export default Frame;
